import { ElementRef, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    PersonsOrganisationDto,
    PreferencesService,
    UserService,
    UserStatus
} from '@flyfreely-portal-ui/flyfreely';
import {
    RegisteredWidgets,
    SelectedWidgets,
    WidgetManager,
    WidgetStatus
} from '@flyfreely-portal-ui/widget';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { Angulartics2 } from 'angulartics2';
import {
    distinctUntilChanged,
    filter,
    ReplaySubject,
    Subject,
    takeUntil
} from 'rxjs';

@Injectable()
export class ManageDashboardWidgetsService extends WidgetManager {
    private availableWidgetsSubject = new ReplaySubject<WidgetStatus>();
    private scrollToListenerSubject = new Subject<string>();

    public availableWidgets$ = this.availableWidgetsSubject.asObservable();
    public scrollToListener$ = this.scrollToListenerSubject.asObservable();

    private widgetElements: { [widgetIdentifier: string]: ElementRef | null } =
        {};
    private selectedWidgets: SelectedWidgets = {};

    private organisation: PersonsOrganisationDto | null = null;
    private personalOrganisation: PersonsOrganisationDto | null = null;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        public registeredWidgets: RegisteredWidgets,
        private userService: UserService,
        private sharedService: WorkspaceStateService,
        private preferencesService: PreferencesService,
        private angulartics2: Angulartics2,
        private activatedRoute: ActivatedRoute
    ) {
        super();
        this.userService.userChange$
            .pipe(
                filter(c => c.type === UserStatus.LOGGED_IN),
                distinctUntilChanged(),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(() => {
                this.personalOrganisation =
                    userService.getPersonalOrganisation();
            });

        this.sharedService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.selectedWidgets = {};
                if (this.organisation != null) {
                    this.loadPreferences(this.organisation.id);
                }
            });

        this.activatedRoute.fragment
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(fragment => {
                if (fragment != null && this.widgetElements[fragment]) {
                    this.scrollTo(fragment);
                }
            });
    }

    ngOnDestroy() {
        this.availableWidgetsSubject.complete();
        this.scrollToListenerSubject.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    public registerWidget(widgetId: string, element: ElementRef) {
        this.widgetElements[widgetId] = element;
        if (this.activatedRoute.snapshot.fragment === widgetId) {
            setTimeout(() => this.scrollTo(widgetId), 1000);
        }
    }

    public unregisterWidget(widgetId: string) {
        this.widgetElements[widgetId] = null;
    }

    public scrollTo(widgetId: string) {
        if (this.widgetElements[widgetId] == null) {
            return;
        }
        this.widgetElements[widgetId].nativeElement.scrollIntoView({
            behavior: 'smooth'
        });

        this.angulartics2.eventTrack.next({
            action: 'scrollTo',
            properties: {
                category: 'sidebar',
                label: widgetId,
                screen: 'dashboard'
            }
        });
    }

    private refreshAvailableWidgets() {
        if (this.registeredWidgets == null) {
            this.availableWidgetsSubject.next({});
            return;
        }
        const updatedAvailableWidgets: WidgetStatus =
            this.registeredWidgets.registeredWidgets.reduce(
                (acc, reg) => ({
                    ...acc,
                    [reg.widgetIdentifier]: {
                        available:
                            reg.isAvailableForUser != null &&
                            this.personalOrganisation != null
                                ? reg.isAvailableForUser(
                                      this.personalOrganisation
                                  )
                                : // Assert that organisation won't be null if this function has been called.
                                  false ||
                                  reg.isAvailable(
                                      this
                                          .organisation as PersonsOrganisationDto
                                  ),
                        selected:
                            this.selectedWidgets[reg.widgetIdentifier] !== false
                    }
                }),
                {} as WidgetStatus
            );

        this.availableWidgetsSubject.next(updatedAvailableWidgets);
    }
    private loadPreferences(organisationId: number) {
        this.preferencesService
            .findPreferences('menu', organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (preferences: any) => {
                    this.selectedWidgets = preferences ?? {};
                    this.refreshAvailableWidgets();
                },
                error: () => {
                    this.refreshAvailableWidgets();
                }
            });
    }

    public changeSelectedWidgets(selectedWidgets: SelectedWidgets) {
        this.selectedWidgets = selectedWidgets;
        this.refreshAvailableWidgets();

        if (this.organisation != null) {
            this.preferencesService
                .updatePreferences(
                    'menu',
                    this.organisation.id,
                    selectedWidgets
                )
                .subscribe();
        }
    }
}
